import React from 'react';
import { Box, Container, Typography, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import Header from '../Layouts/Header';
import Footer from '../Layouts/Footer';

const PageContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: '#FFFFFF'
}));

const ContentContainer = styled(Container)(({ theme }) => ({
  flex: 1,
  paddingTop: '60px',
  paddingBottom: '60px',
  maxWidth: '900px'
}));

const PageTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '36px',
  fontWeight: 700,
  color: '#1F2937',
  textAlign: 'center',
  marginBottom: '16px'
}));

const PageSubtitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '16px',
  fontWeight: 400,
  color: '#6B7280',
  textAlign: 'center',
  marginBottom: '48px'
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '20px',
  fontWeight: 600,
  color: '#1F2937',
  marginTop: '32px',
  marginBottom: '16px'
}));

const SectionText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '15px',
  fontWeight: 400,
  color: '#4B5563',
  lineHeight: '24px',
  marginBottom: '12px'
}));

const BulletList = styled('ul')(({ theme }) => ({
  margin: '12px 0',
  paddingLeft: '24px',
  '& li': {
    fontFamily: 'Inter, sans-serif',
    fontSize: '15px',
    fontWeight: 400,
    color: '#4B5563',
    lineHeight: '24px',
    marginBottom: '8px'
  }
}));

const PrivacyPolicyPage = () => {
  return (
    <PageContainer>
      <Header />
      
      <ContentContainer>
        <PageTitle>Privacy Policy</PageTitle>
        <PageSubtitle>Visibeen – Google Business Profile & AI Usage Terms</PageSubtitle>

        <Stack spacing={2}>
          <SectionTitle>1. Your Account & Access</SectionTitle>
          <BulletList>
            <li>By connecting your Google Business Profile (GBP) to Visibeen, you grant us limited access to help manage, audit, and optimize your profile.</li>
            <li>Keep your login credentials secure. Sharing your account may result in suspension.</li>
          </BulletList>

          <SectionTitle>2. How We Help</SectionTitle>
          <BulletList>
            <li>Visibeen provides tools to audit your GBP, calculate your Visibeen Score, suggest tasks, and compare with competitors.</li>
            <li>AI recommendations are advisory only. You must review and approve all suggestions before taking action.</li>
          </BulletList>

          <SectionTitle>3. AI & Content Use</SectionTitle>
          <BulletList>
            <li>AI may generate posts, replies, or optimization ideas, but errors or inaccuracies may occur.</li>
            <li>Since AI Prompts are highly tested but since its machine model & Visibeen is not responsible for any outcomes resulting from AI recommendations, including posted content.</li>
          </BulletList>

          <SectionTitle>4. Your Responsibilities</SectionTitle>
          <BulletList>
            <li>Ensure all AI suggestions and actions comply with Google policies and applicable laws.</li>
            <li>Maintain accurate business information in your profile.</li>
            <li>Verify all content before posting.</li>
          </BulletList>

          <SectionTitle>5. Limitation of Liability</SectionTitle>
          <SectionText>Visibeen is not liable for any:</SectionText>
          <BulletList>
            <li>Loss of profile rankings, reviews, or visibility</li>
            <li>Suspension or penalties by Google</li>
            <li>Errors, omissions, or damages from AI-generated content</li>
            <li>Any financial or reputational loss</li>
            <li>You are responsible for all decisions made based on Visibeen recommendations.</li>
          </BulletList>

          <SectionTitle>6. Payment Authorization & Mandate Activation</SectionTitle>
          <BulletList>
            <li>By subscribing to Visibeen, you authorize us to initiate recurring payments through NPCI-approved e-mandate or UPI autopay systems.</li>
            <li>You acknowledge that the mandate activation is required to access services, including Google Business Profile management, AI recommendations, and Visibeen™ score features.</li>
            <li>Mandate activation confirms your consent for automatic deduction of subscription fees on the agreed schedule.</li>
          </BulletList>

          <SectionTitle>7. Refunds</SectionTitle>
          <BulletList>
            <li>All payments are non-refundable, except if Visibeen fails to provide access to services.</li>
            <li>Canceling stops future billing but does not refund the current cycle.</li>
          </BulletList>

          <SectionTitle>8. Indemnity</SectionTitle>
          <SectionText>
            You agree to protect Visibeen from any claims, damages, or losses arising from your use of the platform, including AI recommendations and GBP actions.
          </SectionText>

          <SectionTitle>9. Updates</SectionTitle>
          <BulletList>
            <li>These terms may be updated as our platform or AI tools evolve.</li>
            <li>Continued use of Visibeen implies acceptance of the latest terms.</li>
          </BulletList>
        </Stack>
      </ContentContainer>

      <Footer />
    </PageContainer>
  );
};

export default PrivacyPolicyPage;
